import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import {
  Currency,
  EnabledWallet,
} from '@sorare/core/src/__generated__/globalTypes';
import { MonetaryInput } from '@sorare/core/src/atoms/inputs/MonetaryInput';
import { Block } from '@sorare/core/src/atoms/layout/Block';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { isFiatCurrency } from '@sorare/core/src/lib/fiat';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { WalletPaymentMethod } from '@sorare/core/src/lib/paymentMethod';

import {
  setReceiveAmount,
  setSendAmount,
} from '#src/components/directOffer/NewOfferBuilder/actions.ts';
import type { StateProps } from '#src/components/directOffer/NewOfferBuilder/types.ts';
import { FeesDetailsTooltip } from '#src/components/offer/FeesDetailsTooltip/index.tsx';
import { useCalculateDirectOfferMarketFee } from '#src/hooks/offers/useCalculateDirectOfferMarketFee.ts';
import { useStateOffer } from '#src/hooks/offers/useStateOffer.ts';
import {
  MarketFeeStatus,
  isMarketFeeEnabled,
} from '#src/hooks/useMarketFeesHelperStatus.ts';

import type { AmountInput_AnyCardInterface } from './__generated__/index.graphql.ts';

const InputContainer = styled(Block)<{ $disabled: boolean }>`
  padding: var(--double-unit);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-200);
  ${props =>
    props.$disabled &&
    css`
      background-color: var(--c-nd-100);
    `}
`;
const MarketFeeHelper = styled.div`
  display: flex;
  gap: var(--unit);
`;

export const AmountInput = <D extends AmountInput_AnyCardInterface>({
  state,
  dispatch,
  receiver,
  marketFeeStatus = MarketFeeStatus.DISABLED,
}: StateProps<D> & {
  receiver?: boolean;
  marketFeeStatus?: MarketFeeStatus;
}) => {
  const {
    walletPreferences: { onlyShowFiatCurrency, enabledWallets },
  } = useCurrentUserContext();
  const { currency, fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();
  const getMarketFeeAmount = useCalculateDirectOfferMarketFee();
  const {
    sendAmount,
    receiveAmount,
    sendAmountCurrency,
    receiveAmountCurrency,
    paymentMethod,
  } = state;
  const { receiveMinimumPrice, sendMinimumPrice } = useStateOffer(state);

  const referenceCurrency = receiver
    ? receiveAmountCurrency
    : sendAmountCurrency;
  const amount = receiver ? receiveAmount : sendAmount;
  const minAmount = receiver ? receiveMinimumPrice : sendMinimumPrice;

  const marketFeeAmount = getMarketFeeAmount(amount);

  const { main: feesMainAmount } = useAmountWithConversion({
    monetaryAmount: marketFeeAmount,
    primaryCurrency: isFiatCurrency(referenceCurrency)
      ? Currency.FIAT
      : Currency.ETH,
  });

  const localCallback = useCallback(
    (newAmount: MonetaryAmount) => {
      if (receiver) {
        const newMarketFeesAmount =
          newAmount.wei > 0 && getMarketFeeAmount(newAmount);
        dispatch(setReceiveAmount(newAmount, newMarketFeesAmount || undefined));
        return;
      }
      dispatch(setSendAmount(newAmount));
    },
    [receiver, dispatch, getMarketFeeAmount]
  );

  const disabled = useMemo(() => {
    if (receiver) {
      if (state.sendAmount.isPositive) return true;
    } else if (state.receiveAmount.isPositive) return true;
    return false;
  }, [receiver, state.receiveAmount.isPositive, state.sendAmount.isPositive]);

  const marketFeeEnabled = isMarketFeeEnabled(marketFeeStatus);

  const showMarketFeesHelper =
    marketFeeEnabled && amount.isPositive && !!receiver;

  return (
    <div>
      <InputContainer variant="square" border="around" $disabled={disabled}>
        <MonetaryInput
          defaultValue={amount}
          cryptocurrency={cryptocurrency}
          fiatCurrency={fiatCurrency}
          onChange={localCallback}
          defaultCurrency={
            isFiatCurrency(referenceCurrency) ? Currency.FIAT : currency
          }
          error={minAmount.gt(amount)}
          placeholder="0"
          disabled={disabled}
          onlyShowFiatCurrency={
            isFiatCurrency(referenceCurrency) &&
            ((paymentMethod === WalletPaymentMethod.FIAT_WALLET &&
              enabledWallets?.length === 1 &&
              enabledWallets[0] === EnabledWallet.FIAT) ||
              onlyShowFiatCurrency)
          }
        />
        {!!showMarketFeesHelper && (
          <>
            <MarketFeeHelper>
              <BodyS color="var(--c-nd-600)">
                <FormattedMessage defaultMessage="Market fee (incl. tax, if applicable):" />
              </BodyS>
              <FeesDetailsTooltip
                monetaryAmount={amount}
                marketFeeMonetaryAmount={marketFeeAmount}
                marketFeeStatus={marketFeeStatus}
                referenceCurrency={referenceCurrency}
              />
            </MarketFeeHelper>
            <BodyS color="var(--c-nd-600)">
              {marketFeeStatus === MarketFeeStatus.PARTIALLY_ENABLED ? (
                <FormattedMessage
                  defaultMessage="Up to {marketFeeAmount}"
                  values={{ marketFeeAmount: feesMainAmount }}
                />
              ) : (
                feesMainAmount
              )}
            </BodyS>
          </>
        )}
        {!!disabled && (
          <LabelS style={{ marginTop: 8 }}>
            <FormattedMessage defaultMessage="You cannot both receive and send funds in the same trade" />
          </LabelS>
        )}
      </InputContainer>
    </div>
  );
};

AmountInput.fragments = {
  AnyCardInterface: gql`
    fragment AmountInput_AnyCardInterface on AnyCardInterface {
      slug
      ...useStateOffer_anyCard
    }
    ${useStateOffer.fragments.anyCard}
  ` as TypedDocumentNode<AmountInput_AnyCardInterface>,
};
